import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  computed,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms'

import {
  isEmpty as _isEmpty,
  isEqual as _isEqual,
  isString as _isString,
  isArray,
  isObject,
} from 'lodash'
import { isNil as _isNil } from 'lodash/fp'

import { map, tap } from 'rxjs'

import { FlxStyleModule, FlxThemeModule } from '@flowx/angular-theme'
import { FlxIconModule, FlxSelectModule } from '@flowx/angular-ui-toolkit'
import {
  FormFieldsEnum,
  LabelAccessibilityProps,
  MultiSelectTemplateConfig,
  ProcessActionTriggerEvent,
  ResolvedAction,
  getActionsByTemplateConfigAndTrigger$,
  getCurrentReadOnlyState,
  isUiCallAction,
  processDataValueFromPath$,
  prototypeInstanceMetaToPath,
} from '@flowx/core-sdk'
import { ThemeComponent } from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxFieldOptionService } from '../../services/flx-field-option.service'
import { getDynamicPropSignal, getErrorMessages, getThemeIcon } from '../helpers'

@Component({
  selector: 'flx-multi-select-wrapper',
  imports: [
    CommonModule,
    FlxSelectModule,
    FlxLocalizePipe,
    ReactiveFormsModule,
    FlxThemeModule,
    FlxStyleModule,
    FlxIconModule,
  ],
  templateUrl: './multi-select-wrapper.component.html',
  styleUrl: './multi-select-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxMultiSelectWrapperComponent implements OnInit {
  #injector = inject(Injector)
  #optionsService = inject(FlxFieldOptionService)
  #destroyRef = inject(DestroyRef)
  #cdr = inject(ChangeDetectorRef)

  // actions for the CHANGE trigger – kept in sync with the process-actions store
  // so they arrive even when the node's details resolve after this component mounts.
  #changeActions = signal<ResolvedAction[]>([])

  templateConfig = input.required<MultiSelectTemplateConfig>()
  control = input.required<FormControl>()
  errors = input<ValidationErrors | null>(null)
  showErrors = input<boolean>(false)
  disabledState = input<boolean>(false)

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  accessibility = computed((): LabelAccessibilityProps | null => this.flowxProps()?.accessibility)
  fieldKey = computed(() => {
    const { key, prototypeInstanceMeta } = this.templateConfig()
    return prototypeInstanceMeta
      ? `${prototypeInstanceMetaToPath(prototypeInstanceMeta)}.${key}`
      : key
  })

  readonly = getCurrentReadOnlyState

  multiSelectFormInput = new FormControl([])

  icons = computed(() => ({
    rightIcon: getThemeIcon(ThemeComponent.Select, 'rightIcon'),
    helper: getThemeIcon(ThemeComponent.HelperTooltip, 'helperIcon'),
    selected: getThemeIcon(ThemeComponent.MultiSelectOption, 'selectedIcon'),
    unselected: getThemeIcon(ThemeComponent.MultiSelectOption, 'unselectedIcon'),
    indeterminate: getThemeIcon(ThemeComponent.MultiSelectOption, 'indeterminateIcon'),
    chips: getThemeIcon(ThemeComponent.Chips, 'clearIcon'),
  }))

  filteredOptions = computed(() => {
    const options = this.options() || []
    const query = this.query()
    if (!query) {
      return options
    }
    return options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()))
  })

  // dynamic props
  label: Signal<string>
  helper: Signal<string>
  placeholder: Signal<string>
  emptyMessage: Signal<string>
  maxSelectable: Signal<string>
  ariaLabel: Signal<string>

  errorMessages: Record<string, Signal<string>>
  options: Signal<Array<{ label: string; value: string; enabled?: boolean }>>
  query = signal<string>(null)

  // true when a uiCall CHANGE action is present (drives readonly/option-enabled state)
  hasChangeAction = computed(() =>
    this.#changeActions().some(({ action }) => isUiCallAction(action))
  )

  readonly elementUniqueId = `${crypto.randomUUID()}`
  readonly labelId = `${this.elementUniqueId}-form-item-label`
  readonly helperId = `${this.elementUniqueId}-helper`
  readonly errorId = `${this.elementUniqueId}-error`

  readonly describedByIds = computed(() => {
    const ids: string[] = []
    const helper = this.helper()
    const showErrors = this.showErrors()
    const errors = this.errors()

    if (showErrors && errors) {
      Object.keys(errors).forEach((_, index) => {
        ids.push(`${this.errorId}${index}`)
      })
    } else if (helper) {
      ids.push(this.helperId)
    }
    return ids.join(' ')
  })

  ngOnInit(): void {
    getActionsByTemplateConfigAndTrigger$(this.templateConfig(), ProcessActionTriggerEvent.CHANGE)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((actions) => this.#changeActions.set(actions))
    // we need to run this in the injection context here because templateConfig is not available in the constructor;
    // also, running in injectionContext will make sure that the observables are properly disposed when the component is destroyed
    runInInjectionContext(this.#injector, () => {
      const config = this.templateConfig()
      const { label, helper, placeholder = '', emptyMessage, maxSelectable } = this.flowxProps()
      this.label = getDynamicPropSignal(label, config)
      this.helper = getDynamicPropSignal(helper, config)
      this.placeholder = getDynamicPropSignal(placeholder, config)
      this.emptyMessage = getDynamicPropSignal(emptyMessage, config)
      this.maxSelectable = getDynamicPropSignal(maxSelectable, config)
      this.errorMessages = getErrorMessages(config)
      this.ariaLabel = computed(() => this.accessibility()?.label)
      this.options = toSignal(
        this.#optionsService
          .getOptionsObs(config, this.control().parent as FormGroup, FormFieldsEnum.MULTI_SELECT)
          .pipe(
            map((options) => {
              if (!isArray(options)) {
                return []
              }
              if (
                options.some(
                  (option) => !isObject(option) || !('label' in option) || !('value' in option)
                )
              ) {
                return []
              }
              return options
            }),
            // Setting enabled to true in case it's not provided
            map((options) =>
              options.map((option) => ({
                ...option,
                enabled: this.readonly()
                  ? this.hasChangeAction()
                  : option.enabled !== undefined
                    ? option.enabled
                    : true,
              }))
            ),
            tap((options) => this.#matchSelectedOptions(options, this.multiSelectFormInput.value))
          )
      )

      if (this.control().disabled) {
        this.multiSelectFormInput.disable({ emitEvent: false })
      }

      // check for changes to the parent
      this.control()
        .statusChanges.pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => {
          if (this.control().disabled && !this.multiSelectFormInput.disabled) {
            this.multiSelectFormInput.disable({ emitEvent: false })
          } else if (!this.control().disabled && this.multiSelectFormInput.disabled) {
            this.multiSelectFormInput.enable({ emitEvent: false })
          }
        })

      // process data value updates
      processDataValueFromPath$(this.templateConfig().processInstanceUuid, this.fieldKey())
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe((processValue: Array<{ value?: string } | string> | null) => {
          const controlValue = this.multiSelectFormInput.value
          const valueToOption = new Map(
            (this.options() ?? []).map((option) => [option.value, option])
          )
          const processValues = Array.isArray(processValue)
            ? processValue
            : !_isNil(processValue)
              ? [processValue]
              : []
          const selectedValues =
            valueToOption.size === 0
              ? processValues
              : processValues
                  .map((val) => (typeof val === 'object' ? val.value : val))
                  .filter((val) => valueToOption.has(val))

          if (
            (!_isEmpty(selectedValues) || !_isEmpty(controlValue)) &&
            !_isEqual(controlValue, selectedValues)
          ) {
            this.multiSelectFormInput.setValue(selectedValues, { emitEvent: false })
            this.#cdr.markForCheck()
          }
        })

      this.multiSelectFormInput.valueChanges
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe((value) => {
          this.control().setValue(value.map((option) => option.value))
          this.control().markAsDirty()
        })
    })
  }

  filter(query: string): void {
    this.query.set(query)
  }

  #matchSelectedOptions(
    options: Array<{ label: string; value: string; enabled: boolean }>,
    selectedOption: Array<{ label: string; value: string; enabled?: boolean } | string> | string
  ): void {
    if (!selectedOption || _isEmpty(selectedOption)) {
      return
    }
    let foundOptions: Array<{ label: string; value: string; enabled: boolean }>
    const optionValues = options.map(({ value }) => value)

    if (_isString(selectedOption)) {
      foundOptions = options.filter(({ value }) => selectedOption === value)
    } else {
      const selectedValues = selectedOption
        .map((selected) => (_isString(selected) ? selected : selected?.value))
        .filter((value) => optionValues.includes(value))

      foundOptions = selectedValues
        .map((value) => options.find((opt) => opt.value === value))
        .filter(Boolean)
    }
    this.multiSelectFormInput.setValue(foundOptions, { emitEvent: false })

    // silent: the control tracks what is displayed, the store is untouched
    this.control().setValue(
      foundOptions.map((option) => option.value),
      { emitEvent: false }
    )
    this.control().updateValueAndValidity({ emitEvent: false })
    this.control().markAsPristine()
  }
}
